import React, { useEffect, useMemo, useState } from "react";

import "./MailThread.css";

// "On <date> <name> <email> wrote:" line (may be wrapped on 2 lines)
const HEADER_END = /(?:^|\n)[ \t]*(On\s[^\n]*(?:\n[^\n]*)?wrote:)[ \t]*\s*$/i;

const AVATAR_COLORS = [
  "#2e8b3d",
  "#8b3fd9",
  "#d6306a",
  "#2f9d5a",
  "#e8590c",
  "#6a3fb5",
  "#1f9d8f",
  "#d6246e",
];

const colorFor = (text = "") =>
  AVATAR_COLORS[
    [...text].reduce((sum, char) => sum + char.charCodeAt(0), 0) %
      AVATAR_COLORS.length
  ];

const initialsOf = (name = "") =>
  name
    .replace(/<[^>]*>/g, "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] || "")
    .join("")
    .toUpperCase() || "?";

// cuts the trailing "On ... wrote:" line off a block of text
const splitHeader = (text) => {
  const trimmed = text.replace(/\s+$/, "");
  const match = trimmed.match(HEADER_END);

  if (!match) return { text: trimmed.trim(), header: "" };

  return {
    text: trimmed.slice(0, match.index).trim(),
    header: match[1].replace(/\s*\n\s*/g, " ").trim(),
  };
};

// "On Thu, Oct 22, 2026 at 3:26 PM Name <a@b.com> wrote:" -> sender + date
const parseHeader = (header) => {
  const inner = header
    .replace(/^On\s+/i, "")
    .replace(/\s*wrote:\s*$/i, "")
    .trim();

  const emailMatch = inner.match(/<([^<>]+)>/);

  const before = emailMatch ? inner.slice(0, emailMatch.index).trim() : inner;

  const timeMatch = before.match(/^(.*?\d{1,2}:\d{2}(?:\s?[AP]M)?)\s*(.*)$/i);

  const date = timeMatch ? timeMatch[1].replace(/,\s*$/, "") : before;

  const name = (timeMatch ? timeMatch[2] : "")
    .replace(/^[,\s]+|[,\s]+$/g, "")
    .trim();

  return {
    sender: name || (emailMatch ? emailMatch[1] : "Earlier message"),
    email: emailMatch ? emailMatch[1] : "",
    date,
  };
};

// plain-text body -> [newest, older, older ...]  (null = not a simple chain)
const parseThread = (body) => {
  const lines = String(body || "")
    .replace(/\r/g, "")
    .split("\n");

  const segments = [];
  let depth = 0;

  for (const line of lines) {
    const quoted = line.match(/^\s*((?:>\s*)+)(.*)$/);

    let lineDepth;
    let content;

    if (quoted) {
      lineDepth = (quoted[1].match(/>/g) || []).length;
      content = quoted[2];
    } else if (!line.trim()) {
      // blank line belongs to the block around it
      lineDepth = depth;
      content = "";
    } else {
      lineDepth = 0;
      content = line;
    }

    const last = segments[segments.length - 1];

    if (!last || last.depth !== lineDepth) {
      segments.push({ depth: lineDepth, lines: [] });
    }

    segments[segments.length - 1].lines.push(content);
    depth = lineDepth;
  }

  if (segments.length < 2) return null;

  // only a clean chain (0, 1, 2 ...) is split; inline replies stay as one
  if (!segments.every((segment, index) => segment.depth === index)) {
    return null;
  }

  const messages = [];
  let meta = null;

  segments.forEach((segment) => {
    const { text, header } = splitHeader(segment.lines.join("\n"));

    messages.push({ ...(meta || {}), text });

    meta = header ? parseHeader(header) : null;
  });

  return messages;
};

// only the new text of a mail (without the quoted history below it)
export const latestText = (body) => {
  const thread = parseThread(body);

  return thread ? thread[0].text : String(body || "").trim();
};

export default function MailThread({ body, older = false }) {
  const thread = useMemo(() => parseThread(body), [body]);
  const [open, setOpen] = useState({});

  // another mail opened -> all older messages collapsed again
  useEffect(() => {
    setOpen({});
  }, [body]);

  // part 1: the opened mail itself (only its own new text)
  if (!older) {
    return (
      <div
        className="erp-email-body"
        style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
      >
        {thread
          ? thread[0].text || "(No new text in this mail)"
          : body || "No plain-text body available for this email."}
      </div>
    );
  }

  // part 2: earlier mails of the chain, each one a separate card
  if (!thread || thread.length < 2) return null;

  return (
    <div className="erp-thread">
      {thread.slice(1).map((message, index) => {
        const isOpen = Boolean(open[index]);
        const sender = message.sender || "Earlier message";

        return (
          <div key={index} className={`erp-thread-msg ${isOpen ? "open" : ""}`}>
            <button
              type="button"
              className="erp-thread-head"
              aria-expanded={isOpen}
              onClick={() =>
                setOpen((previous) => ({
                  ...previous,
                  [index]: !previous[index],
                }))
              }
            >
              <span
                className="erp-thread-avatar"
                style={{ backgroundColor: colorFor(sender) }}
              >
                {initialsOf(sender)}
              </span>

              <span className="erp-thread-main">
                <strong>{sender}</strong>

                {isOpen ? (
                  message.email && <small>&lt;{message.email}&gt;</small>
                ) : (
                  <span className="erp-thread-snippet">
                    {message.text.replace(/\s+/g, " ").slice(0, 120) ||
                      "(empty)"}
                  </span>
                )}
              </span>

              <small className="erp-thread-date">{message.date}</small>
            </button>

            {isOpen && (
              <div className="erp-thread-text">
                {message.text || "(No text)"}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
